import { localStorage } from '@muse-web/storages'

import type { TTheme } from './types/themeTypes'

const THEME_STORAGE_KEY = 'theme'
const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)'

const isTheme = (value: unknown): value is TTheme => value === 'light' || value === 'dark'

const getSystemTheme = (): TTheme =>
    window.matchMedia(DARK_MEDIA_QUERY).matches ? 'dark' : 'light'

// DS dark tokens key off `:root[data-theme='dark']`.
const applyTheme = (theme: TTheme): void => {
    document.documentElement.dataset.theme = theme
}

const readStoredTheme = (): TTheme | null => {
    const parsed: unknown = localStorage.get(THEME_STORAGE_KEY)

    if (isTheme(parsed)) {
        return parsed
    }

    try {
        const raw = window.localStorage.getItem(THEME_STORAGE_KEY)

        return isTheme(raw) ? raw : null
    } catch {
        return null
    }
}

// A persisted choice wins; otherwise follow the OS preference and keep it in sync. Call before render.
export const setupTheme = (): (() => void) => {
    const override = readStoredTheme()

    applyTheme(override ?? getSystemTheme())

    if (override) {
        return () => undefined
    }

    const mediaQuery = window.matchMedia(DARK_MEDIA_QUERY)
    const handleSystemThemeChange = (): void => applyTheme(getSystemTheme())

    mediaQuery.addEventListener('change', handleSystemThemeChange)

    return () => mediaQuery.removeEventListener('change', handleSystemThemeChange)
}
